import { Component, inject, signal, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { UserService } from '../services/user.service';
import { filter, map } from 'rxjs';
import { UpdateCheckService } from '@awo-care/shared-core';
import { BackButtonComponent } from './backButton.component';
import { LogoWithBadgeComponent } from '@awo-care/shared-ui';
import { environment } from '@awo-care/shared-auth';

@Component({
  selector: 'app-navbar',
  host: { '(window:scroll)': 'onWindowScroll()' },
  imports: [MatButtonModule, MatIconModule, RouterModule, BackButtonComponent, LogoWithBadgeComponent],
  template: `
    <nav class="navbar" [class.compact]="isScrolled()" role="navigation" aria-label="Hauptnavigation" i18n-aria-label="@@core.navbar.ariaLabel">
      <div class="nav-section left">
        <app-back-button />
      </div>

      <div class="nav-section center">
        @if (updateCheckService.updateAvailable()) {
        <div class="updateButton" role="status" aria-live="polite">
          <mat-icon color="primary" aria-hidden="true">info</mat-icon>
          <span i18n="@@core.navbar.updateAvailable|Update available text"
            >Update verfügbar</span
          >
          <button
            mat-flat-button
            color="primary"
            (click)="refresh()"
            aria-label="Seite jetzt aktualisieren"
            i18n-aria-label="@@core.navbar.refreshButton.ariaLabel"
          >
            <span i18n="@@core.navbar.updateNow|Update now button"
              >Jetzt aktualisieren</span
            >
          </button>
        </div>
        } @else {
        <button type="button" class="logo-button" (click)="goHome()" aria-label="Zur Startseite" i18n-aria-label="@@core.navbar.homeButton.ariaLabel">
          <lib-logo-with-badge
            [src]="'./shared-assets/icons/awo-care-logo.svg'"
            [alt]="'AWO Care Logo'"
            [logoClass]="'loginIcons'"
            [tier]="environmentTier"
          />
        </button>
        }
      </div>

      <div class="nav-section right">
        @if (userService.isLoggedIn()) {
        <button
          mat-button
          [routerLink]="['/settings/profile']"
          class="profile-button"
          aria-label="Profil öffnen"
          i18n-aria-label="@@core.navbar.profileButton.ariaLabel"
        >
          <div class="avatar-circle">
            <mat-icon aria-hidden="true">person</mat-icon>
          </div>
        </button>
        }
      </div>
    </nav>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      @use '@angular/material' as mat;

      .navbar {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        background-color: #ddd;
        padding: 0.5rem;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 999;
        width: 100%;
        box-sizing: border-box;
        transition: padding 0.3s ease, height 0.3s ease;

        &.compact {
          padding: 0.25rem 0.5rem;
        }
      }

      .nav-section {
        display: flex;
        align-items: center;

        &.left {
          justify-content: flex-start;
        }

        &.center {
          justify-content: center;
        }

        &.right {
          justify-content: flex-end;
        }
      }

      .updateButton {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        height: 40px;

        span {
          font-weight: 400;
        }

        mat-icon {
          font-size: 1.5rem;
          height: 1.5rem;
          width: 1.5rem;
        }

        button {
          margin-right: 0;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
        }
      }

      .buttonArea {
        display: flex;
        align-items: center;

        @include mat.button-overrides(
          (
            text-hover-state-layer-opacity: 0,
          )
        );
      }

      button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem;
        margin-right: 1rem;

        span {
          font-size: 1rem;
          line-height: 1;
        }

        &:hover {
          .avatar-circle {
            background-color: var(--theme-primary-light);
          }
        }
      }

      .auth-button {
        min-width: 100px;
      }

      .profile-button {
        min-width: 40px;
        padding: 0.25rem;
      }

      .avatar-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: var(--theme-primary);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 500;
        text-transform: uppercase;
        user-select: none;
      }

      .logo-button {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;

        &:focus-visible {
          outline: 3px solid var(--theme-accent);
          outline-offset: 2px;
        }
      }

      .loginIcons {
        height: 40px;
        cursor: pointer;
        transition: height 0.3s ease;
        display: block;
      }

      .navbar.compact .loginIcons {
        height: 32px;
      }
    `,
  ],
})
export class NavbarComponent implements OnInit {
  userService = inject(UserService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  updateCheckService = inject(UpdateCheckService);

  title = signal<string>('AWO Care');
  currentRoute = signal<string>(this.router.url);
  isScrolled = signal<boolean>(false);
  environmentTier = environment.tier;

  getUserInitials(): string {
    const email = this.userService.currentUser()?.email;
    if (!email) return '??';

    const localPart = email.split('@')[0];

    if (/^\d/.test(localPart)) {
      return localPart.slice(0, 2);
    }

    const letters = localPart.replace(/[^a-zA-Z]/g, '');
    return letters.slice(0, 2).toUpperCase();
  }

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return {
            url: this.router.url,
            title: route.snapshot.data['title'],
          };
        })
      )
      .subscribe(({ url, title }) => {
        this.currentRoute.set(url);
        if (title) {
          this.title.set(title);
        } else {
          this.title.set('AWO Care');
        }
      });
  }

  ngOnInit(): void {
    this.currentRoute.set(this.router.url);
  }

  onWindowScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.isScrolled.set(scrollTop > 10);
  }


  async refresh() {
    window.location.reload();
  }

  goHome() {
    this.router.navigate([this.userService.isLoggedIn() ? '/care' : '/']);
  }
}
